package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Composable
internal fun DefaultLockBanner(onDuplicate: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.accentSubtle)
            .borderRadius(Radius.md)
            .padding(Spacing.md)
            .styleModifier {
                property("border", "1px solid ${Palette.accentBorder.cssRgb()}")
                property("gap", "12px")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = "🔒",
            modifier = Modifier.ariaHidden().fontSize(18.px),
        )
        Column(modifier = Modifier.styleModifier { property("flex", "1") }) {
            SpanText(
                text = "Default template — read-only",
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary),
            )
            SpanText(
                text = "Read-only starting point. Duplicate it to make your own.",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textSecondary),
            )
        }
        Button(label = "Duplicate", variant = ButtonVariant.Secondary, onClick = onDuplicate)
    }
}

@Composable
internal fun SectionPill(label: String, active: Boolean, enabled: Boolean, onToggle: (Boolean) -> Unit) {
    val bg = if (active) Palette.accentSubtle else Palette.surfaceSubtle
    val borderColor = if (active) Palette.accent else Palette.borderSubtle
    val textColor = if (active) Palette.accent else Palette.textSecondary
    Row(
        modifier = Modifier
            .backgroundColor(bg)
            .borderRadius(Radius.full)
            .padding(topBottom = 6.px, leftRight = 14.px)
            .role("checkbox")
            .ariaLabel(label)
            .attrsModifier { attr("aria-checked", active.toString()) }
            .styleModifier {
                property("border", "1px solid ${borderColor.cssRgb()}")
                property("gap", "6px")
                property("transition", Motion.TRANSITION_HOVER)
                if (enabled) property("cursor", "pointer")
            }
            .then(
                if (enabled) {
                    Modifier
                        .tabIndex(0)
                        .onClick { onToggle(!active) }
                        .onKeyDown { e ->
                            if (e.key == "Enter" || e.key == " ") {
                                e.preventDefault()
                                onToggle(!active)
                            }
                        }
                } else {
                    Modifier
                },
            ),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = if (active) "✓" else "+",
            modifier = Modifier
                .ariaHidden()
                .fontSize(Typography.caption.size)
                .fontWeight(700)
                .color(textColor),
        )
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(textColor),
        )
    }
}

@Composable
internal fun MetaChip(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.caption.size)
            .color(Palette.textTertiary)
            .fontWeight(500)
            .styleModifier { property("font-family", Typography.FONT_MONO) },
    )
}

@Composable
internal fun CardSectionHeader(title: String, subtitle: String) {
    Column(modifier = Modifier.fillMaxWidth().gap(2.px)) {
        SpanText(
            text = title,
            modifier = Modifier
                .fontSize(Typography.h3.size)
                .fontWeight(Typography.h3.weight)
                .color(Palette.textPrimary),
        )
        SpanText(
            text = subtitle,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
    }
}

@Composable
internal fun DividerLine() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", "1px")
                property("background-color", Palette.borderSubtle.cssRgb())
            },
    )
}
